import React from 'react'
import PropTypes from 'prop-types'
import {
  CircularProgress,
  Divider,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  Card,
  CardContent,
} from '@material-ui/core'
import AutocompleteNative from '@material-ui/lab/Autocomplete'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SaveIcon from '@material-ui/icons/Save'

import { Page, Header, SelecionarData, CustomButton, SecaoGenerica } from 'components'
import { useExameComparacao } from './hooks/useExameComparacao'
import { useStyles } from './ExameComparacao.styles'

export const ExameComparacao = (props) => {
  const { route } = props
  const classes = useStyles()

  const {
    estados,
    paises,
    processandoOperacao,
    processandoRegistrosIdentificacao,
    registrosIdentificacao,
    form,
    isFormInvalid,
    handleChangeResultado,
    handleClickAdicionarRegistro,
    handleClickRemoverRegistro,
    handleChangePropRegistroPeloUuid,
    handleEstadoChange,
    handleChangeFiliacoesPeloUuidRegistroeUUidFiliacao,
    handleAdicionarFiliacaoPelloUuidRegistro,
    cpfMask,
    parseDataLocal,
    handleDateChange,
    handleVoltar,
    handleConfirmar,
  } = useExameComparacao()

  const getResultadoLabel = (classificacao) => {
    switch (classificacao) {
      case 'IDENTIFICADO_SISTEMA_POSITIVO':
        return 'FORAM ENCONTRADOS PADRÕES DACTILOSCÓPICOS CONVERGENTES DA FICHA DE IDENTIFICAÇÃO (POSITIVO)'
      case 'IDENTIFICADO_MAIS_DE_UM_PADRAO':
        return 'FOI ENCONTRADO MAIS DE UM PADRÃO DACTILOSCÓPICO'
      case 'BIOM_CONVERGENTE_BIOG_DIVERGENTE':
        return 'INFORMAÇÕES BIOMÉTRICAS CONVERGENTES E BIOGRÁFICAS DIVERGENTES'
      case 'BIOM_DIVERGENTE_BIOG_CONVERGENTE':
        return 'INFORMAÇÕES BIOMÉTRICAS DIVERGENTES E BIOGRÁFICAS CONVERGENTES'
      case 'NAO_IDENTIFICADO_SISTEMA':
        return 'NÃO FORAM ENCONTRADOS PADRÕES DACTILOSCÓPICOS PARA FINS DE IDENTIFICAÇÃO'
      case 'NAO_IDENTIFICADO_SEM_IMPRESSAO_PAPILAR':
        return 'NÃO IDENTIFICADO POR IMPOSSIBILIDADE DE EXTRAÇÃO DE IMPRESSÃO PAPILAR'
      default:
        return ''
    }
  }

  const isResultadoIdentificado =
    form.formState.values.resultado === 'IDENTIFICADO_SISTEMA_POSITIVO' ||
    form.formState.values.resultado === 'IDENTIFICADO_MAIS_DE_UM_PADRAO' ||
    form.formState.values.resultado === 'BIOM_CONVERGENTE_BIOG_DIVERGENTE' ||
    form.formState.values.resultado === 'BIOM_DIVERGENTE_BIOG_CONVERGENTE'

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} />

      {processandoRegistrosIdentificacao ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <>
          <SecaoGenerica titulo="DADOS DO EXAME">
            <Grid container spacing={2} className={classes.corpoFormulario}>
              <Grid item md={12} sm={12} xs={12} className={classes.item}>
                <AutocompleteNative
                  value={form.formState.values.resultado || null}
                  name="resultado"
                  noOptionsText="Sem informações"
                  options={[
                    'IDENTIFICADO_SISTEMA_POSITIVO',
                    'IDENTIFICADO_MAIS_DE_UM_PADRAO',
                    'BIOM_CONVERGENTE_BIOG_DIVERGENTE',
                    'BIOM_DIVERGENTE_BIOG_CONVERGENTE',
                    'NAO_IDENTIFICADO_SISTEMA',
                    'NAO_IDENTIFICADO_SEM_IMPRESSAO_PAPILAR',
                  ]}
                  onChange={handleChangeResultado}
                  getOptionLabel={getResultadoLabel}
                  renderOption={getResultadoLabel}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Selecionar..."
                      variant="outlined"
                      label="RESULTADO DO CONFRONTO"
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={form.hasError('resultado')}
                      helperText={
                        form.hasError('resultado') ? form.formState.errors.resultado[0] : null
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </SecaoGenerica>

          {isResultadoIdentificado && (
            <SecaoGenerica titulo="DADOS DOS REGISTROS ENCONTRADOS" className={classes.secaoRegistros}>
              <Grid container spacing={2} className={classes.corpoFormulario}>
                {registrosIdentificacao.length === 0 ? (
                  <Grid item md={12} sm={12} xs={12} className={classes.item}>
                    <Typography>
                      Nenhum registro de identificação foi adicionado. Forneça os dados de pelo menos 1 registro
                      de identificação encontrado.
                    </Typography>
                  </Grid>
                ) : (
                  registrosIdentificacao.map((registro) => (
                    <Grid item md={12} sm={12} xs={12} key={registro.uuid}>
                      <Card className={classes.card}>
                        <CardContent className={classes.cardContent}>
                          <Grid container spacing={2}>
                            <Grid item container justifyContent="space-between" alignItems="center">
                              <Grid item>
                                <Typography variant="subtitle2">
                                  <b>DADOS DO REGISTRO</b>
                                </Typography>
                              </Grid>
                              <Grid item>
                                <CustomButton
                                  variant="text"
                                  color="default"
                                  onClick={() => handleClickRemoverRegistro(registro.uuid)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </CustomButton>
                              </Grid>
                            </Grid>
                            <Grid item md={12} sm={12} xs={12}>
                              <Divider style={{ marginBottom: 16 }} />
                            </Grid>

                            <Grid className={classes.item} item md={6} sm={6} xs={12}>
                              <AutocompleteNative
                                name="tipoRegistro"
                                fullWidth
                                value={registro.tipoRegistro || null}
                                noOptionsText="Sem informações"
                                options={['Registro Civil', 'Registro Criminal']}
                                onChange={(event, newValue) =>
                                  handleChangePropRegistroPeloUuid(registro.uuid, 'tipoRegistro', newValue)
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="TIPO DE REGISTRO"
                                    placeholder="Selecionar..."
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    required
                                  />
                                )}
                              />
                            </Grid>

                            <Grid className={classes.item} item md={6} sm={6} xs={12}>
                              <TextField
                                fullWidth
                                label="NÚMERO DO PROTOCOLO DE REGISTRO"
                                name="numeroProtocolo"
                                required
                                onChange={(event) =>
                                  handleChangePropRegistroPeloUuid(
                                    registro.uuid,
                                    'numeroProtocolo',
                                    event.target.value
                                  )
                                }
                                value={registro.numeroProtocolo || ''}
                                variant="outlined"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>

                            <Grid className={classes.item} item md={6} sm={6} xs={12}>
                              <TextField
                                fullWidth
                                label="CPF"
                                name="cpf"
                                onChange={(event) =>
                                  handleChangePropRegistroPeloUuid(
                                    registro.uuid,
                                    'cpf',
                                    cpfMask(event.target.value)
                                  )
                                }
                                value={registro.cpf || ''}
                                variant="outlined"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>

                            <Grid className={classes.item} item md={6} sm={6} xs={12}>
                              <TextField
                                fullWidth
                                label="RG"
                                name="rg"
                                required={registro.tipoRegistro === 'Registro Civil'}
                                onChange={(event) =>
                                  handleChangePropRegistroPeloUuid(registro.uuid, 'rg', event.target.value)
                                }
                                value={registro.rg || ''}
                                variant="outlined"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            </Grid>

                            <Grid className={classes.item} item md={6} sm={6} xs={12}>
                              <TextField
                                variant="outlined"
                                label="NOME"
                                fullWidth
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                onChange={(event) =>
                                  handleChangePropRegistroPeloUuid(registro.uuid, 'nome', event.target.value)
                                }
                                value={registro.nome || ''}
                              />
                            </Grid>

                            <Grid className={classes.item} item md={6} sm={6} xs={12}>
                              <TextField
                                variant="outlined"
                                label="ALCUNHA"
                                fullWidth
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                onChange={(event) =>
                                  handleChangePropRegistroPeloUuid(registro.uuid, 'alcunha', event.target.value)
                                }
                                value={registro.alcunha || ''}
                              />
                            </Grid>

                            <Grid item md={12} sm={12} xs={12} className={classes.item}>
                              <Typography variant="body2" color="error">
                                (É obrigatório fornecer o NOME ou a ALCUNHA)
                              </Typography>
                            </Grid>

                            <Grid item md={6} sm={6} xs={12} className={classes.item} style={{ marginTop: '8px' }}>
                              <SelecionarData
                                dataBD={
                                  registro.dataNascimento ? parseDataLocal(registro.dataNascimento) : null
                                }
                                withoutTime
                                required={registro.tipoRegistro === 'Registro Civil'}
                                name={`dataNascimento_${registro.uuid}`}
                                callback={(date) => handleDateChange(registro.uuid, date)}
                                label="DATA DE NASCIMENTO"
                              />
                            </Grid>

                            <Grid item container md={6} sm={6} xs={12} className={classes.item}>
                              <Grid item md={12} sm={12} xs={12}>
                                <Typography variant="body2">
                                  SEXO {registro.tipoRegistro === 'Registro Civil' ? '*' : ''}
                                </Typography>
                              </Grid>
                              <Grid item md={12} sm={12} xs={12}>
                                <FormControl component="fieldset" fullWidth>
                                  <RadioGroup
                                    aria-label="sexo"
                                    name="sexo"
                                    value={registro.sexo || ''}
                                    onChange={(event) =>
                                      handleChangePropRegistroPeloUuid(registro.uuid, 'sexo', event.target.value)
                                    }
                                    style={{ display: 'flex', flexDirection: 'row' }}
                                  >
                                    <FormControlLabel
                                      value="MASCULINO"
                                      control={<Radio />}
                                      label={<Typography variant="body2">MASCULINO</Typography>}
                                    />
                                    <FormControlLabel
                                      value="FEMININO"
                                      control={<Radio />}
                                      label={<Typography variant="body2">FEMININO</Typography>}
                                    />
                                    <FormControlLabel
                                      value="DESCONHECIDO"
                                      control={<Radio />}
                                      label={<Typography variant="body2">DESCONHECIDO</Typography>}
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                            </Grid>

                            <Grid item md={6} sm={6} xs={12} className={classes.item}>
                              {registro.filiacoes?.map((filiacao, index) => (
                                <Grid
                                  item
                                  md={12}
                                  sm={12}
                                  xs={12}
                                  className={classes.filiacao}
                                  key={filiacao.uuid}
                                >
                                  <TextField
                                    value={filiacao.nome || ''}
                                    variant="outlined"
                                    label="FILIAÇÃO"
                                    fullWidth
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    required={registro.tipoRegistro === 'Registro Civil'}
                                    name={`filiacao_${filiacao.uuid}`}
                                    onChange={(event) =>
                                      handleChangeFiliacoesPeloUuidRegistroeUUidFiliacao(
                                        registro.uuid,
                                        filiacao.uuid,
                                        event.target.value
                                      )
                                    }
                                    InputProps={{
                                      endAdornment: index + 1 === registro.filiacoes?.length && (
                                        <InputAdornment position="start">
                                          <Tooltip title="Adicionar filiação">
                                            <Fab
                                              color="primary"
                                              size="small"
                                              onClick={() =>
                                                handleAdicionarFiliacaoPelloUuidRegistro(registro.uuid)
                                              }
                                              className={classes.botaoAddFiliacao}
                                            >
                                              <AddIcon />
                                            </Fab>
                                          </Tooltip>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                              ))}
                            </Grid>

                            <Grid item md={6} sm={6} xs={12} className={classes.item}>
                              <AutocompleteNative
                                name="nacionalidade"
                                fullWidth
                                getOptionLabel={(option) => option.nome || ''}
                                value={registro.nacionalidade || null}
                                noOptionsText="Sem informações"
                                options={paises}
                                onChange={(event, newValue) =>
                                  handleChangePropRegistroPeloUuid(
                                    registro.uuid,
                                    'nacionalidade',
                                    newValue
                                  )
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="NACIONALIDADE"
                                    placeholder="Selecionar..."
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    required={registro.tipoRegistro === 'Registro Civil'}
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item md={6} sm={6} xs={12} className={classes.item}>
                              <AutocompleteNative
                                name="estado"
                                fullWidth
                                getOptionLabel={(option) => option.nome || ''}
                                value={registro.estado || null}
                                noOptionsText="Sem informações"
                                options={estados}
                                getOptionSelected={(option, value) => option.sigla === value.sigla}
                                onChange={(event, newValue) => handleEstadoChange(registro.uuid, newValue)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="ESTADO"
                                    placeholder="Selecionar..."
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    required={registro.tipoRegistro === 'Registro Civil'}
                                  />
                                )}
                              />
                            </Grid>

                            <Grid item md={6} sm={6} xs={12} className={classes.item}>
                              <AutocompleteNative
                                name="cidade"
                                fullWidth
                                getOptionLabel={(option) => option.nome || ''}
                                value={registro.cidade || null}
                                noOptionsText="Sem informações"
                                options={registro.opcoesCidades || []}
                                getOptionSelected={(option, value) => option.sigla === value.sigla}
                                onChange={(event, newValue) =>
                                  handleChangePropRegistroPeloUuid(registro.uuid, 'cidade', newValue)
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="CIDADE"
                                    placeholder="Selecionar..."
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    required={registro.tipoRegistro === 'Registro Civil'}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                )}
                <Grid item md={12} sm={12} xs={12} className={classes.botaoAdicionar}>
                  <CustomButton variant="contained" color="primary" onClick={handleClickAdicionarRegistro}>
                    <AddIcon />
                  </CustomButton>
                </Grid>
              </Grid>
            </SecaoGenerica>
          )}

          <Divider className={classes.divider} />

          <Grid
            container
            justifyContent="space-between"
            spacing={1}
            className={classes.navigationContainer}
          >
            <Grid item>
              <CustomButton variant="outlined" onClick={handleVoltar} startIcon={<ArrowBackIcon />}>
                Voltar
              </CustomButton>
            </Grid>
            <Grid item>
              <CustomButton
                variant="contained"
                color="primary"
                onClick={handleConfirmar}
                disabled={isFormInvalid}
                isLoading={processandoOperacao}
                startIcon={<SaveIcon />}
              >
                Finalizar
              </CustomButton>
            </Grid>
          </Grid>
        </>
      )}
    </Page>
  )
}

ExameComparacao.propTypes = {
  route: PropTypes.shape({
    titulo: PropTypes.string.isRequired,
    secao: PropTypes.string.isRequired,
  }).isRequired,
}
